import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronDown, faPlus, faEdit, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Loader, Stack, Text } from '@mantine/core';
import { API_ENDPOINTS } from '../constants/api';
import { getCropColor } from '../utils/cropColors';
import { isProtocolManager } from '../utils/jwtUtils';
import CropPickerModal from './CropPickerModal';
import CroppedFieldsMap from './CroppedFieldsMap';
import './CroppedFieldsTree.css';

interface CroppedFieldsTreeProps {
  selectedYear: number;
  userToken: string;
  fieldsWithCrops?: any[];
  selectedItem: { type: string; id: string } | null;
  onItemSelect: (item: { type: string; id: string } | null) => void;
  onRefresh?: () => Promise<void>; // Optional refresh function from parent component
  onLightRefresh?: () => Promise<void>; // Light refresh without loading state
  onComponentRefresh?: () => void; // Simple component refresh function
  selectedOrganizationId?: string | null;
  isSuperUser?: boolean;
  engagementLevel?: number;
}

interface FieldWithCrop {
  field_id: string;
  fieldname: string;
  acres: number;
  state: string;
  county: string;
  farm_id: string;
  business_id: string;
  organization_id: string;
  is_current: boolean;
  geojson?: string;
  planting_id?: string;
  year?: number;
  crop_id?: string;
  crop_acres?: number;
  planting_date?: string;
  harvest_date?: string;
  yield?: number;
  expected_yield?: number | null;
  total_synthetic_n?: number | null;
  tillage?: string | null;
  cover_crop_used?: string;
  crop?: string;
  business: string;
  farm: string;
}


const CroppedFieldsTree: React.FC<CroppedFieldsTreeProps> = ({
  selectedYear,
  userToken,
  fieldsWithCrops: propFieldsWithCrops,
  selectedItem,
  onItemSelect,
  onRefresh,
  onLightRefresh,
  onComponentRefresh,
  selectedOrganizationId,
  isSuperUser,
  engagementLevel = 1
}) => {
  const [fieldsWithCrops, setFieldsWithCrops] = useState<FieldWithCrop[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showNonCurrent, setShowNonCurrent] = useState(false);
  const [cropPickerModal, setCropPickerModal] = useState<{
    isOpen: boolean;
    fieldId: string;
    fieldName: string;
    fieldAcres: number;
    mode: 'add' | 'edit';
    existingCropData?: any;
  }>({ isOpen: false, fieldId: '', fieldName: '', fieldAcres: 0, mode: 'add' });

  const [cdlStats, setCdlStats] = useState<{
    isLoading: boolean;
    data: any | null;
    error: string | null;
    fieldId: string | null;
  }>({ isLoading: false, data: null, error: null, fieldId: null });

  // Track what data has been loaded to avoid unnecessary reloads
  const [dataLoaded, setDataLoaded] = useState({
    fieldsWithCrops: false
  });

  // Track the last loaded year to avoid unnecessary reloads
  const lastLoadedYearRef = useRef<number | null>(null);

  // Track the exact parameters we've already fetched data for
  const [lastFetchParams, setLastFetchParams] = useState<{year: number | null}>({year: null});
  
  // Component refresh key for map components
  const [componentRefreshKey, setComponentRefreshKey] = useState(0);

  useEffect(() => {
    //console.log('🔄 CroppedFieldsTree useEffect - propFieldsWithCrops:', propFieldsWithCrops?.length, 'selectedYear:', selectedYear);
    
    // Always load our own data with include_uncropped=true to show all fields
    // The parent's propFieldsWithCrops only contains cropped fields, but we need all fields
    loadData();
  }, [selectedYear]); // Only depend on selectedYear, not propFieldsWithCrops

  // Expand all items when data loads
  useEffect(() => {
    if (fieldsWithCrops.length > 0) {
      const businessUnitIds = Array.from(new Set(fieldsWithCrops.map(f => f.business_id)));
      const farmIds = Array.from(new Set(fieldsWithCrops.map(f => f.farm_id)));
      const allIds = [...businessUnitIds, ...farmIds];
      setExpandedItems(new Set(allIds));
    }
  }, [fieldsWithCrops]);

  // Auto-select first item when data is loaded (if no item is currently selected)
  useEffect(() => {
    // Only auto-select if no item is currently selected and we're not loading
    if (!selectedItem && !isLoading && fieldsWithCrops.length > 0) {
      // Add a small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        // Priority order: Business Unit -> Farm -> Field
        const businessUnits = getFilteredBusinessUnits();
        if (businessUnits.length > 0) {
          // Find the first business unit (prioritize current ones)
          const firstBusinessUnit = businessUnits.find(bu => bu.is_current) || businessUnits[0];
          if (firstBusinessUnit) {
            onItemSelect({ type: 'business', id: firstBusinessUnit.business_id });
            return; // Exit early since we found a business unit
          }
        }

      }, 100); // Small delay to ensure component is fully rendered

      return () => clearTimeout(timer);
    }
  }, [fieldsWithCrops, selectedItem, isLoading, onItemSelect, selectedYear]);

  // Clear selection when year changes to trigger auto-selection for new year
  useEffect(() => {
    if (selectedItem && lastLoadedYearRef.current !== selectedYear) {
      onItemSelect(null);
      // Reset data loaded flag for new year
      setDataLoaded(prev => ({ ...prev, fieldsWithCrops: false }));
    }
  }, [selectedYear, selectedItem, onItemSelect]);

  // Handle case where selected item no longer exists after data refresh
  useEffect(() => {
    if (selectedItem && !isLoading && fieldsWithCrops.length > 0) {
      let itemExists = false;
      
      if (selectedItem.type === 'business') {
        const businessUnits = getFilteredBusinessUnits();
        itemExists = businessUnits.some(bu => bu.business_id === selectedItem.id);
      } else if (selectedItem.type === 'farm') {
        itemExists = fieldsWithCrops.some(f => f.farm_id === selectedItem.id);
      } else if (selectedItem.type === 'field') {
        itemExists = fieldsWithCrops.some(f => f.field_id === selectedItem.id);
      }
      
      // If the selected item no longer exists, clear the selection to trigger auto-selection
      if (!itemExists) {
        onItemSelect(null);
      }
    }
  }, [fieldsWithCrops, selectedItem, isLoading, onItemSelect]);

  const loadData = async () => {
    // Check if we need to load anything
    if (dataLoaded.fieldsWithCrops && lastLoadedYearRef.current === selectedYear) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate calls while already loading
    if (isLoading) {
      return;
    }
    
    // Double-check to prevent race conditions
    if (lastFetchParams.year === selectedYear && dataLoaded.fieldsWithCrops) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // For Super Users, include the selected organization ID in the request
      const organizationParam = isSuperUser && selectedOrganizationId 
        ? `&organization_id=${selectedOrganizationId}`
        : '';
        
      const response = await fetch(`${API_ENDPOINTS.FIELDS_WITH_CROPS}?year=${selectedYear}&include_uncropped=true${organizationParam}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFieldsWithCrops(data);
        setDataLoaded(prev => ({ ...prev, fieldsWithCrops: true }));
        lastLoadedYearRef.current = selectedYear;
        setLastFetchParams({ year: selectedYear });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh data (force reload)
  const refreshData = async () => {
    // If parent component provides refresh function, use it
    if (onRefresh) {
      await onRefresh();
      return;
    }
    
    // Otherwise, use local refresh logic
    setDataLoaded(prev => ({ ...prev, fieldsWithCrops: false }));
    await loadData();
  };

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const getFieldCropColor = (fieldId: string): string => {
    const fieldWithCrop = fieldsWithCrops.find(f => f.field_id === fieldId);
    if (!fieldWithCrop?.crop) return '#d8d8d8'; // Default color for non-cropped fields
    
    return getCropColor(fieldWithCrop.crop);
  };

  const getCropAcres = (field: FieldWithCrop): number => {
    if (field.crop_acres) {
      return typeof field.crop_acres === 'string' ? parseFloat(field.crop_acres) : field.crop_acres;
    }
    return typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0;
  };

  const handleToggleNonCurrent = useCallback(() => {
    setShowNonCurrent(!showNonCurrent);
  }, [showNonCurrent]);

  const handleExpandAll = useCallback(() => {
    const businessUnitIds = Array.from(new Set(fieldsWithCrops.map(f => f.business_id)));
    const farmIds = Array.from(new Set(fieldsWithCrops.map(f => f.farm_id)));
    const allIds = [...businessUnitIds, ...farmIds];
    setExpandedItems(new Set(allIds));
  }, [fieldsWithCrops]);



  const handleCollapseAll = useCallback(() => {
    setExpandedItems(new Set());
  }, []);

  // Filter and sort functions - all items are automatically sorted alphabetically by name
  const getFilteredBusinessUnits = useCallback(() => {
    
    const businessUnits = Array.from(new Set(fieldsWithCrops.map(f => f.business_id)))
      .map(businessId => {
        const firstField = fieldsWithCrops.find(f => f.business_id === businessId);
        return {
          business_id: businessId,
          business: firstField?.business || '',
          is_current: firstField?.is_current || true
        };
      });
  
    
    let filtered = businessUnits;
    if (!showNonCurrent) {
      filtered = businessUnits.filter(bu => bu.is_current);
    }
    return filtered.sort((a, b) => a.business.localeCompare(b.business));
  }, [fieldsWithCrops, showNonCurrent]);

  const getFilteredFarms = useCallback((businessUnitId: string) => {
    
    const farmsForBusiness = Array.from(new Set(
      fieldsWithCrops.filter(f => f.business_id === businessUnitId).map(f => f.farm_id)
    ))
    .map(farmId => {
      const firstField = fieldsWithCrops.find(f => f.farm_id === farmId);
      return {
        farm_id: farmId,
        farm: firstField?.farm || '',
        is_current: firstField?.is_current || true
      };
    });
    
    let filtered = farmsForBusiness;
    if (!showNonCurrent) {
      filtered = farmsForBusiness.filter(farm => farm.is_current);
    }
    return filtered.sort((a, b) => a.farm.localeCompare(b.farm));
  }, [fieldsWithCrops, showNonCurrent]);

  const getFilteredFields = useCallback((farmId: string) => {
    let fieldsForFarm = fieldsWithCrops.filter(field => field.farm_id === farmId);
    if (!showNonCurrent) {
      fieldsForFarm = fieldsForFarm.filter(field => field.is_current);
    }
    return fieldsForFarm.sort((a, b) => a.fieldname.localeCompare(b.fieldname));
  }, [fieldsWithCrops, showNonCurrent]);

  const handleAddCrop = (fieldId: string, fieldName: string, fieldAcres: number) => {
    setCropPickerModal({ isOpen: true, fieldId, fieldName, fieldAcres, mode: 'add' });
  };

  const handleCropAdded = () => {
    console.log('🌾 Crop added - refreshing data');
    
    // Increment local refresh key for map components
    setComponentRefreshKey(prev => prev + 1);
    
    // Prioritize data refresh functions to ensure CroppedFieldsGrid gets updated
    if (onRefresh) {
      onRefresh();
    } else if (onLightRefresh) {
      onLightRefresh();
    } else {
      // Fallback to local data refresh
      loadData();
    }
    
    // Also trigger component refresh for map components
    if (onComponentRefresh) {
      onComponentRefresh();
    }
  };

  const handleEditCroppedField = (fieldId: string) => {
    const field = fieldsWithCrops.find(f => f.field_id === fieldId);
    if (field) {
      setCropPickerModal({
        isOpen: true,
        fieldId,
        fieldName: field.fieldname,
        fieldAcres: typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0,
        mode: 'edit',
        existingCropData: {
          planting_id: field.planting_id,
          crop_id: field.crop_id,
          acres: field.crop_acres || field.acres,
          planting_date: field.planting_date,
          harvest_date: field.harvest_date,
          yield: field.yield,
          expected_yield: field.expected_yield,
          total_synthetic_n: field.total_synthetic_n,
          tillage: field.tillage,
          cover_crop_used: field.cover_crop_used,
          tdisc_designation: field.tdisc_designation,
          tdisc_calculated_at: field.tdisc_calculated_at,
          tdisc_pending: field.tdisc_pending,
          tillage_event_count: field.tillage_event_count,
        }
      });
    }
  };

  const handleValidateCrop = async (fieldId: string) => {
    try {
      const field = fieldsWithCrops.find(f => f.field_id === fieldId);
      if (!field || !field.geojson) {
        setCdlStats({
          isLoading: false,
          data: null,
          error: 'Field has no boundary geometry',
          fieldId: null
        });
        return;
      }

      // Validate year - CropScape releases each year's data in January of the following year
      // So if we're in 2024, 2024 data won't be available until January 2025
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      // Latest available year: previous year (since current year's data releases in January of next year)
      // Example: In 2024, 2023 data is the latest available. In January 2025, 2024 data becomes available.
      const latestAvailableYear = currentYear - 1;
      const earliestYear = 1997;
      
      if (selectedYear < earliestYear || selectedYear > latestAvailableYear) {
        const yearRange = latestAvailableYear === earliestYear 
          ? `${earliestYear}` 
          : `${earliestYear}-${latestAvailableYear}`;
        
        let errorMessage = `CropScape data is only available for years ${yearRange}. Selected year: ${selectedYear}.`;
        
        // Add helpful note about data release timing
        if (selectedYear >= currentYear) {
          errorMessage += ` Note: CropScape releases each year's crop layer data in January of the following year. ${currentYear} data will be available in January ${currentYear + 1}.`;
        } else if (selectedYear < earliestYear) {
          errorMessage += ` The earliest available year is ${earliestYear}.`;
        }
        
        setCdlStats({
          isLoading: false,
          data: null,
          error: errorMessage,
          fieldId: fieldId  // Keep the fieldId so the error message displays
        });
        return;
      }
      setCdlStats({
        isLoading: true,
        data: null,
        error: null,
        fieldId
      });
      // Parse GeoJSON to extract coordinates
      let geometry: any;
      try {
        geometry = typeof field.geojson === 'string' 
          ? JSON.parse(field.geojson) 
          : field.geojson;
      } catch (error) {
        throw new Error('Invalid GeoJSON format');
      }

      // Extract polygon coordinates directly
      let points: number[][] = [];
      
      if (geometry.type === 'Feature') {
        geometry = geometry.geometry;
      }

      if (geometry.type === 'Polygon' && geometry.coordinates) {
        // Get all coordinates from the exterior ring
        points = geometry.coordinates[0].map((coord: number[]) => [coord[0], coord[1]]);
      } else if (geometry.type === 'MultiPolygon' && geometry.coordinates) {
        // Get all coordinates from the first polygon's exterior ring
        const firstPolygon = geometry.coordinates[0];
        if (firstPolygon && firstPolygon[0]) {
          points = firstPolygon[0].map((coord: number[]) => [coord[0], coord[1]]);
        }
      } else {
        throw new Error('Unsupported geometry type. Only Polygon and MultiPolygon are supported.');
      }

      if (points.length === 0) {
        throw new Error('Could not extract coordinates from field geometry');
      }

      // Call CDL stats API
      const response = await fetch(API_ENDPOINTS.RAVAH_CARBON_CDL_STATS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          year: selectedYear,
          points: points,
          format: 'json'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      
      setCdlStats({
        isLoading: false,
        data,
        error: null,
        fieldId
      });
    } catch (error: any) {
      console.error('Error validating crop with CDL:', error);
      setCdlStats({
        isLoading: false,
        data: null,
        error: error.message || 'Failed to validate crop',
        fieldId
      });
    }
  };

  // Check if there are any fields at all
  const hasAnyFields = getFilteredBusinessUnits().some(bu => 
    getFilteredFarms(bu.business_id).some(farm => 
      getFilteredFields(farm.farm_id).length > 0
    )
    );


  // Show no fields message only after data has loaded and there are no fields
  if (isLoading || !dataLoaded.fieldsWithCrops) {
    return (
      <div className="cropped-fields-tree">
        <div className="loading-message">
          <h3>Loading ...</h3>
        </div>
        </div>
    );
   }  else if (!hasAnyFields) {
    return (
      <div className="cropped-fields-tree">
        <div className="no-fields-message">
          <h3>No Fields Found</h3>
          <p>You haven't set up any farms and fields yet.</p>
          <p>Go to the <strong>Set up Farms and Fields</strong> tab to complete your setup process.</p>
        </div>
      </div>
    );
  } else if (!dataLoaded.fieldsWithCrops) {
    return (
      <div className="cropped-fields-tree">
        <div className="loading-message">
          <h3>Loading fields and crops...</h3>
          <p>Please wait while we fetch your data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cropped-fields-tree">
      {/* Full-screen loading overlay when validating crop with CDL */}
      {cdlStats.isLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '40px 50px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              minWidth: '320px',
            }}
          >
            <Stack align="center" spacing="md">
              <Loader size="xl" />
              <Text size="lg" fw={600}>Validating Crop with CropScape</Text>
              <Text size="sm" c="dimmed">Please wait while we fetch data from CropScape...</Text>
            </Stack>
          </div>
        </div>
      )}
      <div className="tree-container">
        <div className="tree-content">
          <div className="tree-items-container">
            {getFilteredBusinessUnits().map((businessUnit) => (
            <div key={businessUnit.business_id} className="tree-item business-item">
              <div className="tree-item-content">
                <button
                  className={`expand-button ${expandedItems.has(businessUnit.business_id) ? 'expanded' : ''}`}
                  onClick={() => toggleExpanded(businessUnit.business_id)}
                >
                  <FontAwesomeIcon
                    icon={expandedItems.has(businessUnit.business_id) ? faChevronDown : faChevronRight}
                  />
                </button>
                <span
                  className="tree-item-name"
                  onClick={() => onItemSelect({ type: 'business', id: businessUnit.business_id })}
                >
                  {businessUnit.business}
                </span>

              </div>
              
                              {/* Farms */}
                {expandedItems.has(businessUnit.business_id) && (
                  <div className="tree-children">
                    {getFilteredFarms(businessUnit.business_id).map((farm) => (
                      <div key={farm.farm_id} className="tree-item farm-item">
                        <div className="tree-item-content">
                          <button
                            className={`expand-button ${expandedItems.has(farm.farm_id) ? 'expanded' : ''}`}
                            onClick={() => toggleExpanded(farm.farm_id)}
                          >
                            <FontAwesomeIcon
                              icon={expandedItems.has(farm.farm_id) ? faChevronDown : faChevronRight}
                            />
                          </button>
                          <span
                            className="tree-item-name"
                            onClick={() => onItemSelect({ type: 'farm', id: farm.farm_id })}
                          >
                            {farm.farm}
                          </span>

                        </div>
                        
                        {/* Fields */}
                        {expandedItems.has(farm.farm_id) && (
                          <div className="tree-children">
                            {getFilteredFields(farm.farm_id).map((field) => (
                              <div 
                                key={field.field_id} 
                                className={`tree-item field-item ${selectedItem?.type === 'field' && selectedItem?.id === field.field_id ? 'selected' : ''}`}
                              >
                                <div 
                                  className="tree-item-content"
                                  style={{ borderLeftColor: getFieldCropColor(field.field_id), borderLeftWidth: '7px' }}
                                >
                                  <span
                                    className="tree-item-name"
                                    onClick={() => onItemSelect({ type: 'field', id: field.field_id })}
                                  >
                                    {field.fieldname}
                                    {field.crop && (
                                      <span className="field-crop-inline">
                                        {` - ${field.crop} (${getCropAcres(field).toFixed(1)} acres)`}
                                      </span>
                                    )}
                                  </span>
                                  <div className="tree-item-actions">
                                    {selectedItem?.type === 'field' && selectedItem?.id === field.field_id && (isSuperUser || isProtocolManager(userToken)) && (
                                      <button
                                        className="validate-crop-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleValidateCrop(field.field_id);
                                        }}
                                        title="Validate Crop with CropScape"
                                        disabled={cdlStats.isLoading && cdlStats.fieldId === field.field_id}
                                      >
                                        <FontAwesomeIcon icon={faSearch} />
                                      </button>
                                    )} 
                                    {field.crop ? (
                                      <button
                                        className="btn-edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditCroppedField(field.field_id);
                                        }}
                                        title="Edit Crop Details"
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </button>
                                    ) : (
                                      <button
                                        className="add-crop-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddCrop(field.field_id, field.fieldname, typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0);
                                        }}
                                        title="Add Crop"
                                      >
                                        <FontAwesomeIcon icon={faPlus} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ))}
          </div>

          {/* Toggle Panel */}
          <div className="toggle-panel">
            <div className="toggle-buttons">
              <button
                className={`toggle-btn ${!showNonCurrent ? 'active' : ''}`}
                onClick={handleToggleNonCurrent}
              >
                {showNonCurrent ? 'Hide Non-Current' : 'Show Non-Current'}
              </button>
              <button
                className={`toggle-btn ${expandedItems.size > 0 ? 'active' : ''}`}
                onClick={expandedItems.size > 0 ? handleCollapseAll : handleExpandAll}
              >
                {expandedItems.size > 0 ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel for Details */}
        <div className="tree-details">
          {selectedItem ? (
            <div className="details-content">
              {selectedItem.type === 'field' && (
                <div className="field-details">
                  <h3>Field Details</h3>
                  {(() => {
                    const field = fieldsWithCrops.find(f => f.field_id === selectedItem.id);
                    if (!field) return <p>Field not found</p>;
                    
                    // Get all fields from the same farm to show in map
                    const allFieldsFromFarm = fieldsWithCrops.filter(f => f.farm_id === field.farm_id);

                    return (
                      <div>
                        {field.crop ? (
                          <div className="field-info">
                            <p><strong>Field Name:</strong> {field.fieldname}</p>
                            <p><strong>Crop:</strong> {field.crop}</p>
                            <p><strong>Acres:</strong> {getCropAcres(field).toFixed(1)} acres</p>
                            {field.planting_date && (
                              <p><strong>Planting Date:</strong> {new Date(field.planting_date).toLocaleDateString()}</p>
                            )}
                          </div>
                        ) : (
                          <div className="field-info">
                            <p><strong>Name:</strong> {field.fieldname}</p>
                            <p><strong>Area:</strong> {(typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0).toFixed(1)} acres</p>
                            <p><strong>Location:</strong> {field.county}, {field.state}</p>
                          </div>
                        )}

                        {/* CDL Validation Results */}
                        {cdlStats.fieldId === field.field_id && (
                          <div className="cdl-validation-results" style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                            <h4 style={{ marginTop: 0, marginBottom: '10px' }}>CropScape Crop Validation</h4>
                            {cdlStats.isLoading && (
                              <p style={{ color: '#666' }}>Validating crop with USDA CropScape data...</p>
                            )}
                            {cdlStats.error && (
                              <div style={{ color: '#dc3545', marginBottom: '10px' }}>
                                <strong>Error:</strong> {cdlStats.error}
                              </div>
                            )}
                            {cdlStats.data && !cdlStats.isLoading && (
                              <div>
                                {cdlStats.data.success ? (
                                  <div>
                                    <p style={{ marginBottom: '10px' }}>
                                      <strong>Total Crop Types Detected:</strong> {cdlStats.data.rows?.length || 0}
                                    </p>
                                    {cdlStats.data.rows && cdlStats.data.rows.length > 0 ? (
                                      <div>
                                        <strong>Detected Crops:</strong>
                                        <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#e9ecef' }}>
                                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #dee2e6' }}>Crop</th>
                                              <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>Count</th>
                                              <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>Acres</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {cdlStats.data.rows.map((row: any, index: number) => (
                                              <tr key={index} style={{ backgroundColor: field.crop && row.category === field.crop ? '#d4edda' : 'white' }}>
                                                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                                  <span style={{ 
                                                    display: 'inline-block', 
                                                    width: '16px', 
                                                    height: '16px', 
                                                    backgroundColor: row.color || '#CCCCCC',
                                                    marginRight: '8px',
                                                    borderRadius: '2px',
                                                    verticalAlign: 'middle'
                                                  }}></span>
                                                  {row.category || 'Unknown'}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>{row.count || 0}</td>
                                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #dee2e6' }}>{(row.acreage || 0).toFixed(2)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                        {field.crop && (() => {
                                          // Normalize crop names for comparison (handle singular/plural, case-insensitive)
                                          const normalizeCropName = (name: string): string => {
                                            if (!name) return '';
                                            const normalized = name.toLowerCase().trim();
                                            // Handle common singular/plural variations
                                            if (normalized.endsWith('s') && normalized.length > 1) {
                                              return normalized.slice(0, -1); // Remove trailing 's'
                                            }
                                            return normalized;
                                          };
                                          
                                          const fieldCropNormalized = normalizeCropName(field.crop);
                                          const hasMatch = cdlStats.data.rows.some((r: any) => {
                                            const detectedCropNormalized = normalizeCropName(r.category);
                                            return fieldCropNormalized === detectedCropNormalized || 
                                                   detectedCropNormalized === fieldCropNormalized + 's' ||
                                                   fieldCropNormalized === detectedCropNormalized + 's';
                                          });
                                          
                                          return (
                                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: hasMatch ? '#d4edda' : '#f8d7da', borderRadius: '4px' }}>
                                              {hasMatch ? (
                                                <strong style={{ color: '#155724' }}>✓ Match: Your crop "{field.crop}" was detected by CropScape</strong>
                                              ) : (
                                                <strong style={{ color: '#721c24' }}>⚠ No Match: Your crop "{field.crop}" was not detected. Detected crops are listed above.</strong>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <p style={{ color: '#666' }}>No crop data detected for this field.</p>
                                    )}
                                  </div>
                                ) : (
                                  <p style={{ color: '#666' }}>No validation data available.</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="cropped-fields-tree-field-map-container">
                          <CroppedFieldsMap
                            fields={allFieldsFromFarm}
                            selectedFieldId={field.field_id}
                            mapStyle="satellite"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedItem.type === 'farm' && (
                <div className="farm-details">
                  <h3>Farm Details</h3>
                  {(() => {
                    const farmFields = fieldsWithCrops.filter(f => f.farm_id === selectedItem.id);
                    if (farmFields.length === 0) return <p>Farm not found</p>;

                    const farm = farmFields[0];
                    const totalAcres = farmFields.reduce((sum, field) => sum + (typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0), 0);

                    return (
                      <div>
                        <div className="farm-info">
                          <p><strong>Name:</strong> {farm.farm}</p>
                          <p><strong>Total Fields:</strong> {farmFields.length}</p>
                          <p><strong>Total Area:</strong> {totalAcres.toFixed(1)} acres</p>
                        </div>
                        <div className="cropped-fields-tree-farm-map-container">
                          <CroppedFieldsMap
                            fields={farmFields}
                            mapStyle="satellite"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedItem.type === 'business' && (
                <div className="business-details">
                  <h3>Business Unit Details</h3>
                  {(() => {
                    const businessFields = fieldsWithCrops.filter(f => f.business_id === selectedItem.id);
                    if (businessFields.length === 0) return <p>Business Unit not found</p>;

                    const businessUnit = businessFields[0];
                    const businessFarms = Array.from(new Set(businessFields.map(f => f.farm_id)));
                    const totalAcres = businessFields.reduce((sum, field) => sum + (typeof field.acres === 'number' ? field.acres : parseFloat(field.acres) || 0), 0);

                    return (
                      <div>
                        <div className="business-info">
                          <p><strong>Name:</strong> {businessUnit.business}</p>
                          <p><strong>Total Farms:</strong> {businessFarms.length}</p>
                          <p><strong>Total Fields:</strong> {businessFields.length}</p>
                          <p><strong>Total Area:</strong> {totalAcres.toFixed(1)} acres</p>
                        </div>
                        <div className="cropped-fields-tree-business-map-container">
                          <CroppedFieldsMap
                            fields={businessFields}
                            mapStyle="satellite"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="details-content">
              <h3>Details</h3>
              <p>Click on a Business, Farm or Field name to view the details</p>
            </div>
          )}
        </div>
      </div>

      {/* Crop Picker Modal */}
                    <CropPickerModal
                isOpen={cropPickerModal.isOpen}
                onClose={() => setCropPickerModal({ isOpen: false, fieldId: '', fieldName: '', fieldAcres: 0, mode: 'add' })}
                fieldId={cropPickerModal.fieldId}
                fieldName={cropPickerModal.fieldName}
                fieldAcres={cropPickerModal.fieldAcres}
                year={selectedYear}
                userToken={userToken}
                onCropAdded={handleCropAdded}
                mode={cropPickerModal.mode}
                selectedOrganizationId={selectedOrganizationId || undefined}
                engagementLevel={engagementLevel}
                existingCropData={cropPickerModal.existingCropData}
              />
    </div>
  );
};

export default CroppedFieldsTree;
